import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Plus, Edit2, Trash2, X, Check, Users } from 'lucide-react';
import {
  fetchRoles,
  fetchPermissionKeys,
  createRole,
  updateRole,
  deleteRole,
  RoleData,
  PermissionInfo,
} from '../services/api.ts';
import './Roles.css';

interface RoleFormData {
  name: string;
  description: string;
  permissions: string[];
}

const emptyForm: RoleFormData = { name: '', description: '', permissions: [] };

const Roles: React.FC = () => {
  const [roles, setRoles] = useState<RoleData[]>([]);
  const [permissionKeys, setPermissionKeys] = useState<PermissionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleData | null>(null);
  const [formData, setFormData] = useState<RoleFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [rolesData, permsData] = await Promise.all([fetchRoles(), fetchPermissionKeys()]);
      setRoles(rolesData);
      setPermissionKeys(permsData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateModal = () => {
    setEditingRole(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (role: RoleData) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: [...role.permissions],
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRole(null);
    setFormData(emptyForm);
  };

  const togglePermission = (key: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((p) => p !== key)
        : [...prev.permissions, key],
    }));
  };

  const toggleAllPermissions = () => {
    if (formData.permissions.length === permissionKeys.length) {
      setFormData((prev) => ({ ...prev, permissions: [] }));
    } else {
      setFormData((prev) => ({ ...prev, permissions: permissionKeys.map((p) => p.key) }));
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      if (editingRole) {
        await updateRole(editingRole.id, {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          permissions: formData.permissions,
        });
      } else {
        await createRole({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          permissions: formData.permissions,
        });
      }
      closeModal();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteRole(id);
      setDeleteConfirm(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete role');
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="roles-page">
        <div className="roles-loading">
          <div className="spinner-large" />
          <p>Loading roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="roles-page">
      <div className="roles-header">
        <div className="roles-header-left">
          <Shield size={28} />
          <div>
            <h1>Roles & Permissions</h1>
            <p>Manage access control for admin users</p>
          </div>
        </div>
        <button className="roles-btn-primary" onClick={openCreateModal}>
          <Plus size={18} />
          Create Role
        </button>
      </div>

      {error && (
        <div className="roles-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={16} /></button>
        </div>
      )}

      <div className="roles-grid">
        {roles.map((role) => (
          <div key={role.id} className={`role-card ${role.is_system ? 'system' : ''}`}>
            <div className="role-card-header">
              <div className="role-card-title">
                <h3>{role.name}</h3>
                {role.is_system && <span className="role-badge system">System</span>}
              </div>
              <div className="role-card-actions">
                <button className="role-action-btn edit" onClick={() => openEditModal(role)} title="Edit">
                  <Edit2 size={16} />
                </button>
                {!role.is_system && (
                  <button
                    className="role-action-btn delete"
                    onClick={() => setDeleteConfirm(role.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            {role.description && <p className="role-card-desc">{role.description}</p>}

            <div className="role-card-perms">
              {permissionKeys.map((perm) => (
                <span
                  key={perm.key}
                  className={`perm-badge ${role.permissions.includes(perm.key) ? 'active' : 'inactive'}`}
                >
                  {role.permissions.includes(perm.key) ? <Check size={12} /> : null}
                  {perm.label}
                </span>
              ))}
            </div>

            <div className="role-card-footer">
              <Users size={14} />
              <span>{role.user_count} user{role.user_count !== 1 ? 's' : ''}</span>
            </div>

            {deleteConfirm === role.id && (
              <div className="role-delete-confirm">
                <p>Delete this role?</p>
                <div className="role-delete-actions">
                  <button className="roles-btn-danger" onClick={() => handleDelete(role.id)}>Delete</button>
                  <button className="roles-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="roles-modal-overlay" onClick={closeModal}>
          <div className="roles-modal" onClick={(e) => e.stopPropagation()}>
            <div className="roles-modal-header">
              <h2>{editingRole ? 'Edit Role' : 'Create Role'}</h2>
              <button className="roles-modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <div className="roles-modal-body">
              <div className="roles-form-group">
                <label>Role Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Content Manager"
                  disabled={editingRole?.is_system}
                />
              </div>

              <div className="roles-form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this role"
                  rows={2}
                />
              </div>

              <div className="roles-form-group">
                <div className="perms-header">
                  <label>Permissions</label>
                  <button className="roles-btn-link" onClick={toggleAllPermissions}>
                    {formData.permissions.length === permissionKeys.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="perms-grid">
                  {permissionKeys.map((perm) => (
                    <label key={perm.key} className="perm-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(perm.key)}
                        onChange={() => togglePermission(perm.key)}
                      />
                      <span className="perm-checkbox-label">{perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="roles-modal-footer">
              <button className="roles-btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="roles-btn-primary"
                onClick={handleSave}
                disabled={saving || !formData.name.trim()}
              >
                {saving ? 'Saving...' : editingRole ? 'Update Role' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
